// Read-only role × permission matrix (rbac-restore R1, D51/D52).
// Everything comes from GET /workspaces/{id}/roles: the permission rows, the four role
// columns and the count. Nothing is copied from the gateway's role table, so the page
// cannot drift from what the server enforces. Roles are fixed; there is nothing to edit.
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { qk } from '../api/queries'
import type { Permission, Role, WorkspaceRolesResponse } from '../api/types'
import { useSession } from '../context/SessionContext'
import { Badge, PageHeader, QueryState } from '../components/ui'
import { Icon } from '../components/Icon'
import { permissionLabel } from '../components/format'

const ROLE_ORDER: Role[] = ['owner', 'admin', 'member', 'viewer']
const ROLE_LABEL: Record<Role, string> = { owner: 'Owner', admin: 'Admin', member: 'Member', viewer: 'Viewer' }

export function PermissionsView() {
  const { workspaceId, session } = useSession()
  const myRole = session?.workspace.role
  const q = useQuery({
    queryKey: qk.workspaceRoles(workspaceId),
    queryFn: () => apiFetch<WorkspaceRolesResponse>(`/workspaces/${workspaceId}/roles`),
    enabled: !!workspaceId,
  })
  const data = q.data
  // Columns: the four fixed roles, in a stable order, as the server returned them.
  const roles = data ? ROLE_ORDER.filter((r) => r in data.roles) : []
  const sets = Object.fromEntries(roles.map((r) => [r, new Set(data?.roles[r] ?? [])])) as Record<Role, Set<Permission>>
  // Rows: every permission any role holds, most widely held first, then by name.
  const rows = data
    ? [...new Set(roles.flatMap((r) => data.roles[r]))].sort((a, b) => {
        const byReach = roles.filter((r) => sets[r].has(b)).length - roles.filter((r) => sets[r].has(a)).length
        return byReach || a.localeCompare(b)
      })
    : []

  return (
    <div className="page">
      <PageHeader
        title="Permissions"
        count={data?.count}
        description={
          data ? (
            <>
              What each role can do in this workspace: {data.count} permissions across {roles.length} roles,
              read from the gateway. Roles are fixed and can’t be edited.
            </>
          ) : (
            'What each role can do in this workspace, read from the gateway.'
          )
        }
      />
      <QueryState isLoading={q.isLoading} error={q.error} what="workspace permissions" onRetry={() => q.refetch()}>
        {data && (
          <>
            {myRole && (
              <p className="perm-you" role="status">
                Your role: <strong>{ROLE_LABEL[myRole]}</strong> ({data.roles[myRole]?.length ?? 0} of {data.count}{' '}
                permissions)
              </p>
            )}
            <div className="table-wrap">
              <table className="table table--dense perm-matrix" aria-label="Role permissions">
                <thead>
                  <tr>
                    <th scope="col">Permission</th>
                    {roles.map((r) => (
                      <th
                        key={r}
                        scope="col"
                        className={'perm-col' + (r === myRole ? ' perm-col--you' : '')}
                        aria-current={r === myRole ? 'true' : undefined}
                      >
                        {ROLE_LABEL[r]}
                        <span className="cell-sub">{data.roles[r].length}</span>
                        {r === myRole && <Badge tone="info">You</Badge>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p}>
                      <th scope="row" className="perm-name">
                        <span className="cell-strong">{permissionLabel(p)}</span>
                        <code className="cell-sub">{p}</code>
                      </th>
                      {roles.map((r) => {
                        const yes = sets[r].has(p)
                        return (
                          <td
                            key={r}
                            className={'perm-cell' + (r === myRole ? ' perm-col--you' : '')}
                            aria-label={`${ROLE_LABEL[r]}: ${yes ? 'allowed' : 'not allowed'}`}
                          >
                            {yes ? <Icon name="check" size={14} /> : <span className="muted" aria-hidden="true">–</span>}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </QueryState>
    </div>
  )
}
